import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * CLE-174 — lightweight anchored popover used by the inline task editors
 * (due date / priority / assignee / status). It renders into a portal, closes
 * on outside-click or Escape, and flips above the anchor when there isn't room
 * below. The app has no Radix/headless library, so this stays dependency-free
 * and styled with the existing `.ui-popover-panel` token class.
 */

interface PopoverProps {
  // The trigger element. It is wrapped in a span that toggles the panel.
  trigger: (props: {
    open: boolean;
    toggle: () => void;
    ref: (el: HTMLElement | null) => void;
  }) => ReactNode;
  children: (close: () => void) => ReactNode;
  /** Panel width in px (default 240). */
  width?: number;
  /**
   * Match the panel width to the trigger's rendered width (min `width` px).
   * Used by the labeled form Select so the menu lines up with its field.
   */
  matchTriggerWidth?: boolean;
  align?: 'start' | 'end';
  onOpenChange?: (open: boolean) => void;
}

export function Popover({
  trigger,
  children,
  width = 240,
  matchTriggerWidth = false,
  align = 'start',
  onOpenChange,
}: PopoverProps) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  // Opening down anchors the panel's top under the trigger; opening up anchors
  // its BOTTOM just above the trigger, so the gap never depends on how tall
  // the panel turns out to be.
  const [pos, setPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
  } | null>(null);
  // The first placement has to guess the panel height (it is not mounted yet);
  // once it is, measure it and place again with the real height.
  const measured = useRef(false);

  function setOpenState(next: boolean) {
    setOpen(next);
    onOpenChange?.(next);
  }

  const close = () => setOpenState(false);
  const toggle = () => setOpenState(!open);

  function place() {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const panelW = matchTriggerWidth ? Math.max(rect.width, width) : width;
    const panelH = panelRef.current?.offsetHeight ?? 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < panelH + 12 && rect.top > panelH;
    let left = align === 'end' ? rect.right - panelW : rect.left;
    // Keep within the viewport horizontally.
    left = Math.max(8, Math.min(left, window.innerWidth - panelW - 8));
    setPos(
      openUp
        ? { bottom: window.innerHeight - rect.top + 6, left, width: panelW }
        : { top: rect.bottom + 6, left, width: panelW },
    );
  }

  useLayoutEffect(() => {
    if (!open) {
      measured.current = false;
      return;
    }
    place();
  }, [open, align, width, matchTriggerWidth]);

  // Second pass, before paint: the panel now exists, so the up/down decision
  // uses its real height instead of the 280px guess.
  useLayoutEffect(() => {
    if (!open || !pos || measured.current || !panelRef.current) return;
    measured.current = true;
    place();
  }, [open, pos]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorRef.current?.contains(t)) return;
      close();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      {trigger({ open, toggle, ref: (el) => (anchorRef.current = el) })}
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="ui-popover-panel fixed z-[60] overflow-hidden"
            style={{ top: pos.top, bottom: pos.bottom, left: pos.left, width: pos.width }}
            onClick={(e) => e.stopPropagation()}
          >
            {children(close)}
          </div>,
          document.body,
        )}
    </>
  );
}
